import logError from 'src/data/services/logError';
import { useRef } from 'react';
import { DefaultTheme, NavigationContainer } from '@react-navigation/native';
import Colors from 'src/core/utils/Colors';
import RootNavigator from 'src/presentation/navigation/navigators/RootNavigator';
import LinkingConfiguration from 'src/presentation/navigation/LinkingConfiguration';
import { activateEventFromPath } from 'src/presentation/helpers/EventDeeplinkHandler';
import Constants from 'src/core/utils/Constants';
import { navigationRef } from 'src/domain/stores/useNavigationStore';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useGalleryStore } from 'src/domain/stores/useGalleryStore';
import { useAnalyticsStore } from 'src/domain/stores/useAnalyticsStore';
import { usePushNavigation } from 'src/presentation/hooks/usePushNavigation';

const theme: typeof DefaultTheme = {
	...DefaultTheme,
	colors: {
		...DefaultTheme.colors,
		background: Colors.white,
	},
};

function Navigation() {
	const routeNameRef = useRef<string | null>(null);
	const activatedCodeRef = useRef<string | null>(null);
	const activatingCodeRef = useRef<string | null>(null);

	const eventUuids = useEventStore((state) => state.eventUuids);
	const activeEventUuid = useEventStore((state) => state.activeEventUuid);
	const fetchGalleries = useGalleryStore((state) => state.fetchGalleries);
	const eventTime = useAnalyticsStore((state) => state.eventTime);

	// #349: route reminder push taps (warm background tap + cold start) to the event detail.
	const { onNavigationReady } = usePushNavigation();

	// A deep URL names its event by code while every screen reads the active event from the store.
	// Doing this only once was enough for a cold load but not for the browser's back button: going
	// from event A to event B and then back to a screen of A restored the route but left B active,
	// so the screen showed the wrong event's pages. The ref keeps it to one activation per code.
	const syncActiveEventWithRoute = () => {
		const route = navigationRef.current?.getCurrentRoute();
		const code = (route?.params as { code?: string } | undefined)?.code;

		if (code === undefined || route?.name === Constants.screenEventDeeplink) return;
		if (activatedCodeRef.current === code || activatingCodeRef.current === code) return;

		// Marked as handled only once the work is done, so a failure can be retried on the next state
		// change. The in-flight ref is what keeps onStateChange from starting a second activation
		// while the first one is still loading the stored events.
		activatingCodeRef.current = code;
		activateEventFromPath(code)
			.then(() => {
				activatedCodeRef.current = code;
			})
			.catch(logError)
			.finally(() => {
				if (activatingCodeRef.current === code) activatingCodeRef.current = null;
			});
	};

	return (
		<NavigationContainer
			linking={LinkingConfiguration}
			ref={navigationRef}
			theme={theme}
			onReady={() => {
				onNavigationReady();
				syncActiveEventWithRoute();
			}}
			onStateChange={() => {
				syncActiveEventWithRoute();

				const currentRouteName = navigationRef?.current?.getCurrentRoute()?.name;

				if (currentRouteName === 'Splash' && routeNameRef.current !== 'Splash') {
					fetchGalleries(eventUuids).catch(logError);
					eventTime(activeEventUuid, 'end');
				}

				routeNameRef.current = currentRouteName ?? null;
			}}
		>
			<RootNavigator />
		</NavigationContainer>
	);
}

export default Navigation;
